import { useEffect } from 'react';
import { CalloutCard } from '@amzn/ring-react-components/csr/components/CalloutCard';
import { Button } from '@amzn/ring-react-components/csr/components/Button';
import { observer } from 'mobx-react';
import { redirectToOnboardingHowVSGWorks, redirectToProfessionalMonitoringSettings } from '$src/utils/redirect';
import useLocationContext from '$src/hooks/useLocationContext';
import useStores from '$src/hooks/useStores';
import OnboardingContainer from '$src/components/OnboardingContainer';
import { handleBackToRingDashboard } from '$src/utils/handleBack';
import aboutImage from '$src/assets/img/onboardingAbout.png';
import { CheckIcon, SecurityCheckIcon } from '@ring-icons';
import armedAway from '$src/assets/img/alarmStatus-armed-away.png';
import armedHome from '$src/assets/img/alarmStatus-armed-home.png';
import disarmed from '$src/assets/img/alarmStatus-disarmed.png';
import AlarmStatusImg from '$src/components/AlarmStatusImg';
import useRedirectBasedOnMonitoringStatus from '../Introduction/useRedirectBasedOnMonitoringStatus';

import './SetupComplete.less';

function SetupComplete() {
  const {
    sessionStore: { user, isMobile },
    locationContextStore: { setOnboardingCompleted },
    modesStore: { isPracticeMode, isPendingMode },
  } = useStores();
  const locationId = useLocationContext();

  useEffect(() => {
    setOnboardingCompleted(user.user_id, locationId);
  }, [user?.user_id, locationId, setOnboardingCompleted]);

  useRedirectBasedOnMonitoringStatus(user.user_id, locationId);

  return (
    <OnboardingContainer
      headerTitle="Setup Complete"
      icon={CheckIcon}
      title={
        isPracticeMode || isPendingMode
          ? 'Your setup for Virtual Security Guard is complete'
          : 'Success! You’re now protected by Virtual Security Guard.'
      }
      subtitle={
        <>
          To start using the service, set your location to <b> Home </b> or <b> Away </b> using the Ring App.
        </>
      }
      primaryText="View Quick Start Guide"
      secondaryText="Go To Ring Dashboard"
      image={aboutImage}
      hideBack
      handleClose={() => handleBackToRingDashboard(isMobile)}
      primaryActionHandler={() => redirectToOnboardingHowVSGWorks(locationId)}
      secondaryActionHandler={() => handleBackToRingDashboard(isMobile)}
    >
      <div className="setup-complete-page">
        {isPracticeMode && (
          <CalloutCard
            data-testid="warning-practice-mode"
            icon={<SecurityCheckIcon width="22px" />}
            status="default"
            title="Your Alarm is currently set to Practice Mode"
          >
            Virtual Security Guard requires active Professional Monitoring, please exit Practice Mode for Virtual Security Guard to work.
          </CalloutCard>
        )}
        {isPendingMode && (
          <CalloutCard
            data-testid="warning-pending-mode"
            icon={<SecurityCheckIcon width="22px" />}
            status="default"
            title="Virtual Security Guard is currently not active due to the following reason"
          >
            Your Professional Monitoring is currently in pending status. Active Professional Monitoring is required to activate Virtual Security
            Guard. Check your Professional Monitoring status{' '}
            <Button color="primary" variant="link" onClick={() => redirectToProfessionalMonitoringSettings(locationId, isMobile)}>
              here
            </Button>
            .
          </CalloutCard>
        )}
        <div className="setup-complete-page__alarm-status-box">
          <AlarmStatusImg
            title={
              <>
                Virtual Security Guard <b>Inactive</b>
              </>
            }
            image={disarmed}
          />
          <AlarmStatusImg
            title={
              <>
                Virtual Security Guard <b>Active</b>
              </>
            }
            image={armedHome}
          />
          <AlarmStatusImg
            title={
              <>
                Virtual Security Guard <b>Active</b>
              </>
            }
            image={armedAway}
          />
        </div>
      </div>
    </OnboardingContainer>
  );
}

export default observer(SetupComplete);
